import { useQuery } from '@tanstack/react-query'
import { Banner, Button, NavigationLink, Skeleton } from '@shared/design-system/ui'
import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { fetchKycVerification, verificationKeys, type KycVerification } from '../api/verification'
import { getOnboardingAction } from '../lib/onboarding-action'
import { noticeAction } from './receipts-account-notice.css'

function receivingGuidance(view: KycVerification) {
  const next = view.onboarding.nextStep
  const action = getOnboardingAction(next)
  if (action.kind === 'support')
    return {
      title: 'Seu cadastro precisa de atenção',
      description:
        'Consulte a orientação de suporte no cadastro. As comissões registradas continuam no seu histórico.',
      ...action,
    }
  if (!view.linked)
    return {
      title: 'Conclua seu cadastro para receber comissões',
      description: 'Você pode indicar e acumular comissões enquanto conclui o cadastro.',
      href: '/onboarding',
      action: 'Concluir cadastro',
    }
  if (action.kind === 'review')
    return {
      title: 'Cadastro em análise',
      description:
        'Você pode continuar acumulando comissões durante a análise. A aprovação não confirma o repasse nem o depósito bancário.',
      ...action,
    }
  if (next.owner === 'seller') {
    return {
      title: 'Há pendências no cadastro de recebimento',
      description:
        'Suas comissões continuam sendo registradas. Confira a pendência para seguir com a liberação dos repasses.',
      ...action,
    }
  }
  return {
    title: view.eligibility.approved ? 'Cadastro aprovado' : 'Confira as condições de recebimento',
    description:
      'O repasse depende da sua conta estar apta a receber e da conclusão do processamento. O valor aguardando repasse não está disponível para saque.',
    ...action,
  }
}

export function ReferralReceivingNotice() {
  const enabled = useFeatureFlag('referralProgram')
  const query = useQuery({
    queryKey: verificationKeys.view,
    queryFn: fetchKycVerification,
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: 'always',
  })
  if (!enabled) return null
  if (query.isPending) return <Skeleton height={100} data-testid="referral-receiving-loading" />
  const view = query.data
  const uncertain =
    !view ||
    query.isError ||
    (view.onboarding.nextStep.owner !== 'support' &&
      (view.eligibility.syncFailure ||
        view.verification?.syncFailure ||
        (view.linked && !view.eligibility.observedAt)))
  const guidance = !uncertain && view ? receivingGuidance(view) : null
  return (
    <Banner
      data-testid="referral-receiving-notice"
      tone={
        uncertain || !view?.eligibility.approved || view.onboarding.nextStep.owner === 'support'
          ? 'warning'
          : 'info'
      }
      title={guidance?.title ?? 'Não foi possível confirmar a situação do cadastro'}
    >
      {guidance?.description ??
        'Atualize a consulta ou confira o cadastro. Esta consulta não altera suas comissões e não confirma a liberação de repasses.'}
      <span className={noticeAction}>
        <NavigationLink
          data-testid="referral-receiving-cadastro-link"
          href={guidance?.href ?? '/onboarding'}
        >
          {guidance?.action ?? 'Conferir cadastro'}
        </NavigationLink>
      </span>
      <span className={noticeAction}>
        <Button
          variant="secondary"
          data-testid="referral-receiving-refresh-btn"
          disabled={query.isFetching}
          onClick={() => void query.refetch()}
        >
          Atualizar situação
        </Button>
      </span>
    </Banner>
  )
}
